/**
 * Client-safe helpers for locale persistence and resolution.
 *
 * The frontend has multiple legacy locale surfaces (route, next-intl storage,
 * SILVERTIGER cookie, DOM attributes). Keep the resolution logic centralized so
 * the visible UI locale and the locale sent to backend stay aligned.
 */

import { defaultLocale, locales, type Locale } from '@/i18n';
import { SILVERTIGER_LOCALE_COOKIE } from '@/const/locale';

const DEFAULT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year
export const LOCALE_STORAGE_KEY = 'NEXT_LOCALE';
export const MANUAL_LOCALE_STORAGE_KEY = 'manually-chosen-locale';
export const LOCALE_REDIRECTED_KEY = 'locale-redirected';

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  try {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const part of cookies) {
      const [rawKey, ...rawValue] = part.trim().split('=');
      if (rawKey === name) {
        return decodeURIComponent(rawValue.join('='));
      }
    }
  } catch {
    // ignore parsing errors
  }

  return null;
}

function setCookie(name: string, value: string, options?: { maxAgeSeconds?: number }) {
  if (typeof document === 'undefined') return;

  const maxAge = options?.maxAgeSeconds ?? DEFAULT_COOKIE_MAX_AGE_SECONDS;
  const encoded = encodeURIComponent(value);

  // Keep it simple + broadly compatible.
  document.cookie = `${name}=${encoded}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
}

export function storeLocalePreference(locale: Locale) {
  if (!locales.includes(locale)) return;

  // Persist to both locale cookies so next-intl and legacy consumers stay aligned.
  setCookie(LOCALE_STORAGE_KEY, locale);
  setCookie(SILVERTIGER_LOCALE_COOKIE, locale);

  try {
    sessionStorage.setItem(LOCALE_STORAGE_KEY, locale);
    sessionStorage.setItem(MANUAL_LOCALE_STORAGE_KEY, locale);
    sessionStorage.removeItem(LOCALE_REDIRECTED_KEY);
  } catch {
    // ignore storage errors
  }

  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    localStorage.setItem(MANUAL_LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore storage errors
  }

  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    document.documentElement.setAttribute('data-locale', locale);
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }

  // Notify any listeners (ConnectorIntlProvider listens for this).
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { locale } }));
    } catch {
      // ignore event errors
    }
  }
}

/**
 * Extract locale from a URL pathname like `/vi/connectors/...`.
 *
 * Convention used across the app: `en` is the default and may appear without a
 * locale prefix.
 */
export function getCurrentLocaleFromPath(pathname: string): Locale {
  if (!pathname) return 'en';

  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const firstSegment = normalized.split('/')[1] || '';

  return locales.includes(firstSegment as Locale) ? (firstSegment as Locale) : 'en';
}

export function normalizeLocaleCandidate(candidate?: string | null): Locale | null {
  if (!candidate) return null;

  const normalized = candidate.trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) return null;

  const exact = locales.find(locale => locale === normalized);
  if (exact) return exact;

  const languageCode = normalized.split('-')[0];
  return locales.includes(languageCode as Locale) ? (languageCode as Locale) : null;
}

export function resolveBrowserLocale(): Locale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  return normalizeLocaleCandidate(window.navigator.language) ?? defaultLocale;
}

function readStorageValue(storage: Storage | undefined, keys: string[]): string | null {
  if (!storage) return null;

  for (const key of keys) {
    try {
      const value = storage.getItem(key);
      if (value) return value;
    } catch {
      // ignore storage access issues
    }
  }

  return null;
}

export function resolvePreferredResponseLocale(): Locale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  const manualLocale =
    readStorageValue(window.sessionStorage, [MANUAL_LOCALE_STORAGE_KEY]) ??
    readStorageValue(window.localStorage, [MANUAL_LOCALE_STORAGE_KEY]);
  const nextIntlLocale =
    readStorageValue(window.sessionStorage, [LOCALE_STORAGE_KEY]) ??
    readStorageValue(window.localStorage, [LOCALE_STORAGE_KEY]);
  const nextIntlCookieLocale = getCookie(LOCALE_STORAGE_KEY);
  const pathLocale = getCurrentLocaleFromPath(window.location.pathname);
  const candidates = [
    manualLocale,
    nextIntlLocale,
    getCookie(SILVERTIGER_LOCALE_COOKIE),
    nextIntlCookieLocale,
    pathLocale,
    document.documentElement.getAttribute('data-locale'),
    document.documentElement.lang,
    navigator.language,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeLocaleCandidate(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return defaultLocale;
}

export function buildPreferredResponseLanguagePayload(): {
  locale: Locale;
  preferred_response_language: Locale;
} {
  const locale = resolvePreferredResponseLocale();
  return {
    locale,
    preferred_response_language: locale,
  };
}
